import React, { useEffect, useRef, useState } from 'react';
import {
  Building,
  Building2,
  Crown,
  FastForward,
  Gem,
  Heart,
  House,
  Lamp,
  Loader2,
  Piano,
  Plane,
  Play,
  Sofa,
  Sparkles,
  Square,
  Sun,
  Sunrise,
  Waves,
  Zap,
  type LucideIcon,
} from 'lucide-react';

export interface LibraryTrack {
  id: string;
  title: string;
  durationSeconds: number;
  storagePath: string;
  previewUrl?: string | null;
}

const TRACK_LOOK: Record<string, { icon: LucideIcon; tile: string }> = {
  'aerial-reveal': { icon: Plane, tile: 'bg-sky-500/20 text-sky-300' },
  'luminous-pulse': { icon: Zap, tile: 'bg-violet-500/20 text-violet-300' },
  'morning-light': { icon: Sunrise, tile: 'bg-amber-500/20 text-amber-300' },
  'coastal-breeze': { icon: Waves, tile: 'bg-cyan-500/20 text-cyan-300' },
  'warm-homely-walkthrough': { icon: House, tile: 'bg-orange-500/20 text-orange-300' },
  'apartment-groove': { icon: Building2, tile: 'bg-indigo-500/20 text-indigo-300' },
  possibility: { icon: Sparkles, tile: 'bg-fuchsia-500/20 text-fuchsia-300' },
  'city-views': { icon: Building, tile: 'bg-slate-400/20 text-slate-200' },
  'bright-morning-groove': { icon: Sun, tile: 'bg-yellow-500/20 text-yellow-300' },
  untitled: { icon: Sofa, tile: 'bg-amber-500/20 text-amber-200' },
  'penthouse-groove': { icon: Crown, tile: 'bg-amber-400/20 text-amber-200' },
  'velvet-hallways': { icon: Lamp, tile: 'bg-rose-500/20 text-rose-300' },
  'piano-house-groove': { icon: Piano, tile: 'bg-rose-400/20 text-rose-200' },
  'forward-pulse': { icon: FastForward, tile: 'bg-emerald-500/20 text-emerald-300' },
  'sleek-groove': { icon: Gem, tile: 'bg-slate-300/20 text-slate-100' },
  'aerial-homes': { icon: House, tile: 'bg-sky-400/20 text-sky-200' },
};

function lookFor(id: string): { icon: LucideIcon; tile: string } {
  return TRACK_LOOK[id] ?? { icon: Sofa, tile: 'bg-amber-500/20 text-amber-200' };
}

function formatLength(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SupabaseClient = any;

export async function fetchRoyaltyFreeTracks(supabase: SupabaseClient): Promise<LibraryTrack[]> {
  const { data, error } = await supabase
    .from('propertycut_music')
    .select('id, title, duration_seconds, storage_path')
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('title', { ascending: true });

  if (error || !data) {
    console.error('Fetch royalty-free music error:', error);
    return [];
  }

  const tracks: LibraryTrack[] = [];
  for (const row of data as Array<{ id: string; title: string; duration_seconds: number; storage_path: string }>) {
    const signed = await supabase.storage
      .from('propertycut-uploads')
      .createSignedUrl(row.storage_path, 60 * 60);
    tracks.push({
      id: row.id,
      title: row.title,
      durationSeconds: row.duration_seconds,
      storagePath: row.storage_path,
      previewUrl: signed.data?.signedUrl ?? null,
    });
  }
  return tracks;
}

export async function downloadRoyaltyFreeTrack(supabase: SupabaseClient, track: LibraryTrack): Promise<File> {
  const { data, error } = await supabase.storage.from('propertycut-uploads').download(track.storagePath);
  if (error || !data) throw error ?? new Error('Could not download that track');
  return new File([data], `${track.title}.mp3`, { type: 'audio/mpeg' });
}

async function loadFavouriteIds(supabase: SupabaseClient): Promise<string[]> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;
  if (!userId) return [];
  const { data, error } = await supabase
    .from('propertycut_music_favourites')
    .select('music_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error || !data) return [];
  return (data as Array<{ music_id: string }>).map((row) => row.music_id);
}

export function LibraryTrackList({
  tracks,
  onAdd,
  addingId,
  addLabel = 'Add',
  supabase,
}: {
  tracks: LibraryTrack[];
  onAdd: (track: LibraryTrack) => void;
  addingId?: string | null;
  addLabel?: string;
  supabase?: SupabaseClient;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [favouriteIds, setFavouriteIds] = useState<string[]>([]);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    loadFavouriteIds(supabase).then((ids) => {
      if (!cancelled) setFavouriteIds(ids);
    });
    return () => {
      cancelled = true;
    };
  }, [supabase]);

  const togglePreview = (track: LibraryTrack) => {
    if (!track.previewUrl) return;
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(track.previewUrl);
    audio.onended = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(track.id);
  };

  const toggleFavourite = async (track: LibraryTrack) => {
    if (!supabase) return;
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) return;
    const already = favouriteIds.includes(track.id);
    setFavouriteIds((current) =>
      already ? current.filter((id) => id !== track.id) : [track.id, ...current.filter((id) => id !== track.id)],
    );
    const { error } = already
      ? await supabase.from('propertycut_music_favourites').delete().eq('user_id', userId).eq('music_id', track.id)
      : await supabase.from('propertycut_music_favourites').insert({ user_id: userId, music_id: track.id });
    if (error) {
      const ids = await loadFavouriteIds(supabase);
      setFavouriteIds(ids);
    }
  };

  if (tracks.length === 0) return null;

  const byId = new Map(tracks.map((track) => [track.id, track]));
  const favourites = favouriteIds.map((id) => byId.get(id)).filter((track): track is LibraryTrack => Boolean(track));
  const favouriteSet = new Set(favourites.map((track) => track.id));
  const rest = tracks.filter((track) => !favouriteSet.has(track.id));

  const renderTrack = (track: LibraryTrack) => {
    const look = lookFor(track.id);
    const Icon = look.icon;
    const busy = addingId === track.id;
    const favourite = favouriteSet.has(track.id);
    return (
      <div key={track.id} className="flex items-center gap-2 rounded-lg bg-slate-800/50 px-2 py-1.5">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${look.tile}`} aria-hidden>
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs text-white">{track.title}</span>
          <span className="block text-[10px] text-slate-500">{formatLength(track.durationSeconds)}</span>
        </span>
        {supabase && (
          <button
            type="button"
            onClick={() => void toggleFavourite(track)}
            className={`rounded-md p-1.5 ${favourite ? 'text-rose-300' : 'text-slate-500 hover:text-rose-200'}`}
            aria-label={favourite ? `Remove ${track.title} from favourites` : `Add ${track.title} to favourites`}
            aria-pressed={favourite}
          >
            <Heart className={`h-3.5 w-3.5 ${favourite ? 'fill-current' : ''}`} />
          </button>
        )}
        <button
          type="button"
          onClick={() => togglePreview(track)}
          disabled={!track.previewUrl}
          className="rounded-md p-1.5 text-slate-400 hover:text-white disabled:opacity-40"
          aria-label={playingId === track.id ? `Stop ${track.title}` : `Play ${track.title}`}
        >
          {playingId === track.id ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => onAdd(track)}
          disabled={Boolean(addingId)}
          className="text-xs text-teal-300 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : addLabel}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {favourites.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] text-slate-500">Favourites</p>
          {favourites.map(renderTrack)}
        </div>
      )}
      {rest.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] text-slate-500">Royalty-free library</p>
          {rest.map(renderTrack)}
        </div>
      )}
    </div>
  );
}
